import { useEffect, useRef, useState } from 'react';
import { AlertTriangle, Info, Shuffle } from 'lucide-react';
import { api } from '@/lib/api';

/**
 * Inline hint shown under a formula editor. Asks the backend whether the
 * formula references fields across unrelated tables and, for document-store
 * sources, suggests the Data-Prep pre-join path. Debounced; renders nothing
 * when there's no concern.
 */
export function FormulaScopeHint({ modelId, formula }) {
  const [warning, setWarning] = useState(null);
  const timer = useRef(null);

  useEffect(() => {
    if (!modelId || !formula || !formula.trim()) { setWarning(null); return; }
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      try {
        const res = await api.analyzeFormula(modelId, formula);
        setWarning(res?.warning || null);
      } catch { /* silent — hint is best-effort */ }
    }, 500);
    return () => timer.current && clearTimeout(timer.current);
  }, [modelId, formula]);

  if (!warning) return null;

  const styles = {
    error: { box: 'bg-rose-500/10 border-rose-500/30 text-rose-200', icon: 'text-rose-400', Icon: AlertTriangle },
    warning: { box: 'bg-amber-500/10 border-amber-500/30 text-amber-100', icon: 'text-amber-400', Icon: Shuffle },
    info: { box: 'bg-primary/10 border-primary/30 text-foreground/80', icon: 'text-primary', Icon: Info },
  };
  const s = styles[warning.level] || styles.info;
  const Icon = s.Icon;

  return (
    <div className={`mt-2 rounded-xl border px-3 py-2.5 flex items-start gap-2 ${s.box}`} data-testid="formula-scope-hint">
      <Icon className={`w-4 h-4 mt-0.5 flex-shrink-0 ${s.icon}`} />
      <div className="text-[11px] leading-relaxed">
        <p className="font-semibold">{warning.title}</p>
        <p className="opacity-90 mt-0.5">{warning.message}</p>
      </div>
    </div>
  );
}
